import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { BrandMark, BRAND } from "../components/Brand";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft, MagnifyingGlass, X, FunnelX,
  ArrowCounterClockwise, Trash as TrashIcon, Warning, CaretLeft, CaretRight,
} from "@phosphor-icons/react";
import { MODULES, PRIORITIES } from "../lib/constants";

const ALL = "__all__";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function Trash() {
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", module: "", priority: "", deleted_by: "" });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmPurge, setConfirmPurge] = useState(null); // null | { ids, count }

  const query = useMemo(() => {
    const p = { page, page_size: pageSize };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters, page, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks/bulk/trash", { params: query });
      setTasks(data.tasks || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      if (data.page && data.page !== page) setPage(data.page);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load trash");
    } finally {
      setLoading(false);
    }
  }, [query, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { setPage(1); setSelectedIds(new Set()); }, [filters]);

  const deletedByOptions = useMemo(() => {
    const seen = new Set();
    tasks.forEach((t) => { if (t.deleted_by) seen.add(t.deleted_by); });
    return Array.from(seen).sort();
  }, [tasks]);

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    const pageIds = tasks.map((t) => t.id);
    const allSel = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSel) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const restore = async (ids) => {
    try {
      const { data } = await api.post("/tasks/bulk/restore", { ids });
      toast.success(`Restored ${data.restored || 0} task${(data.restored || 0) === 1 ? "" : "s"}`);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    }
  };

  const purge = async (ids) => {
    try {
      const { data } = await api.post("/tasks/bulk/purge", { ids });
      toast.success(`Permanently deleted ${data.purged || 0} task${(data.purged || 0) === 1 ? "" : "s"}`);
      setConfirmPurge(null);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    }
  };

  const activeFilterCount = Object.values(filters).filter((v) => v).length;
  const pageIds = tasks.map((t) => t.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someOnPageSelected = pageIds.some((id) => selectedIds.has(id));

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="trash-page">
      <header className="h-16 border-b border-[#E5E5E5] bg-white flex items-center px-6 justify-between shrink-0">
        <div className="flex items-center gap-3">
          <BrandMark />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              PROJECT TRACKER
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Trash</span>
          </div>
        </div>
        <Link to="/">
          <Button variant="outline" size="sm" data-testid="back-to-dashboard-btn" className="h-9">
            <ArrowLeft size={16} weight="bold" className="mr-2" />
            Back to Tracker
          </Button>
        </Link>
      </header>

      <div className="flex-1 p-6 max-w-6xl w-full mx-auto space-y-5">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.3em] text-neutral-500"
              style={{ fontFamily: "IBM Plex Mono, monospace" }}
            >
              Soft-deleted rows
            </p>
            <h1 className="text-3xl font-black tracking-tight mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>
              Trash
            </h1>
            <p className="text-sm text-neutral-500 mt-1">
              Restore accidentally deleted tasks — or permanently purge them to free up space. Audit history is retained even after a permanent delete.
            </p>
          </div>
          <div className="text-xs text-neutral-500">
            <span className="font-mono tabular-nums text-neutral-900" data-testid="trash-total">
              {total}
            </span>{" "}
            in trash{loading ? " · loading..." : ""}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              data-testid="trash-search-input"
              className="pl-9 h-10 bg-white"
              placeholder="Search by ID, title, comments..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
            {filters.search ? (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
                onClick={() => setFilters({ ...filters, search: "" })}
              >
                <X size={14} weight="bold" />
              </button>
            ) : null}
          </div>

          <Select
            value={filters.module || ALL}
            onValueChange={(v) => setFilters({ ...filters, module: v === ALL ? "" : v })}
          >
            <SelectTrigger data-testid="trash-module-filter" className="h-10 w-[150px] bg-white">
              <SelectValue placeholder="All modules" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All modules</SelectItem>
              {MODULES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select
            value={filters.priority || ALL}
            onValueChange={(v) => setFilters({ ...filters, priority: v === ALL ? "" : v })}
          >
            <SelectTrigger data-testid="trash-priority-filter" className="h-10 w-[150px] bg-white">
              <SelectValue placeholder="All priorities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All priorities</SelectItem>
              {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>

          {deletedByOptions.length > 0 ? (
            <Select
              value={filters.deleted_by || ALL}
              onValueChange={(v) => setFilters({ ...filters, deleted_by: v === ALL ? "" : v })}
            >
              <SelectTrigger data-testid="trash-deletedby-filter" className="h-10 w-[220px] bg-white">
                <SelectValue placeholder="Deleted by anyone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Deleted by anyone</SelectItem>
                {deletedByOptions.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : null}

          <Button
            variant="outline"
            size="sm"
            disabled={activeFilterCount === 0}
            onClick={() => setFilters({ search: "", module: "", priority: "", deleted_by: "" })}
            data-testid="trash-clear-filters-btn"
            className="h-10"
          >
            <FunnelX size={16} weight="bold" className="mr-2" />
            Clear
            {activeFilterCount > 0 ? (
              <span className="ml-2 text-xs bg-neutral-900 text-white rounded-full h-5 w-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            ) : null}
          </Button>
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 ? (
          <div
            data-testid="trash-bulk-bar"
            className="flex items-center gap-3 bg-[#387EE6] text-white rounded-lg px-4 py-3"
          >
            <span className="text-sm font-semibold" data-testid="trash-selected-count">
              {selectedIds.size} selected
            </span>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => restore(Array.from(selectedIds))}
              data-testid="trash-bulk-restore-btn"
              className="h-8 bg-white text-neutral-900 hover:bg-neutral-100"
            >
              <ArrowCounterClockwise size={14} weight="bold" className="mr-1.5" />
              Restore
            </Button>
            <Button
              size="sm"
              onClick={() => setConfirmPurge({ ids: Array.from(selectedIds), count: selectedIds.size })}
              data-testid="trash-bulk-purge-btn"
              className="h-8 bg-red-600 hover:bg-red-700 text-white"
            >
              <TrashIcon size={14} weight="bold" className="mr-1.5" />
              Purge permanently
            </Button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="h-8 w-8 flex items-center justify-center rounded hover:bg-white/10 text-white/70"
              aria-label="Clear selection"
              data-testid="trash-bulk-clear-btn"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        ) : null}

        {/* Table */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[#F5F5F5] border-b border-[#E5E5E5]">
                <th className="px-4 py-3 w-[44px]">
                  <input
                    type="checkbox"
                    data-testid="trash-select-all-on-page"
                    aria-label="Select all"
                    checked={allOnPageSelected}
                    ref={(el) => { if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected; }}
                    onChange={toggleAllOnPage}
                    className="cursor-pointer accent-neutral-900"
                  />
                </th>
                {["Module", "Object ID", "Title", "Priority", "Deleted", "Deleted by", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-14 text-center text-sm text-neutral-500">Loading…</td></tr>
              ) : tasks.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-14 text-center text-sm text-neutral-500" data-testid="trash-empty">
                    <div className="flex flex-col items-center gap-2">
                      <TrashIcon size={28} weight="thin" className="text-neutral-400" />
                      Trash is empty.
                    </div>
                  </td>
                </tr>
              ) : tasks.map((t) => {
                const isSelected = selectedIds.has(t.id);
                return (
                <tr
                  key={t.id}
                  data-testid={`trash-row-${t.id}`}
                  className={`border-b border-[#E5E5E5] hover:bg-[#FAFAFA] ${isSelected ? "bg-sky-50/60" : ""}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      data-testid={`trash-select-${t.id}`}
                      checked={isSelected}
                      onChange={() => toggleRow(t.id)}
                      className="cursor-pointer accent-neutral-900"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-800">{t.module || "—"}</td>
                  <td className="px-4 py-3 text-sm font-mono text-neutral-700">{t.object_id || "—"}</td>
                  <td className="px-4 py-3 text-sm text-neutral-900 max-w-[320px] truncate" title={t.title}>
                    {t.title || <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-700">{t.priority || "—"}</td>
                  <td className="px-4 py-3 text-xs text-neutral-600" title={t.deleted_at ? new Date(t.deleted_at).toLocaleString() : ""}>
                    {timeAgo(t.deleted_at)}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-700 font-mono">{t.deleted_by || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => restore([t.id])}
                        data-testid={`trash-restore-btn-${t.id}`}
                        className="h-7 w-7 flex items-center justify-center rounded hover:bg-emerald-50 text-emerald-700"
                        title="Restore"
                      >
                        <ArrowCounterClockwise size={14} weight="bold" />
                      </button>
                      <button
                        onClick={() => setConfirmPurge({ ids: [t.id], count: 1 })}
                        data-testid={`trash-purge-btn-${t.id}`}
                        className="h-7 w-7 flex items-center justify-center rounded hover:bg-red-50 text-red-600"
                        title="Delete permanently"
                      >
                        <TrashIcon size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 ? (
          <div className="flex items-center justify-end gap-2" data-testid="trash-pagination">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              data-testid="trash-prev-btn"
            >
              <CaretLeft size={14} weight="bold" className="mr-1" />
              Prev
            </Button>
            <span className="text-xs text-neutral-500 font-mono tabular-nums px-2">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              data-testid="trash-next-btn"
            >
              Next
              <CaretRight size={14} weight="bold" className="ml-1" />
            </Button>
          </div>
        ) : null}
      </div>

      <AlertDialog open={!!confirmPurge} onOpenChange={(v) => !v && setConfirmPurge(null)}>
        <AlertDialogContent data-testid="trash-purge-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-red-600" />
              Permanently delete {confirmPurge?.count || 0} task{confirmPurge?.count === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action is <span className="font-semibold text-red-700">not reversible</span>. The task rows will be gone forever. Audit history remains accessible in the Activity Log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trash-purge-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => purge(confirmPurge.ids)}
              data-testid="trash-purge-confirm-btn"
              className="bg-red-600 hover:bg-red-700"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
